import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import sumBy from "lodash/sumBy";
import { format } from "date-fns";

import { InfoPlate } from "fe-shared/src/components";

import {
  InvoiceFee,
  InvoiceTransaction,
  InvoiceTransactionType,
  RowInvoice,
} from "../../types/invoice";
import { InvoiceRows } from "../index";
import { formatTransactionNumber } from "../../helpers/format";
import {
  getDeliveryMaterialsInvoiceInfoRows,
  getInvoiceInfoMaterialRows,
  getTransactionFeeRows,
} from "./modules/config";
import { TireIcon } from "../Icons";

import { InvoiceMaterial } from "../../types";
import { formatWeight, priceToFormat } from "../../helpers/invoice";

import styles from "./InvoiceInfoDetails.module.scss";

interface Props {
  transactions: InvoiceTransaction[];
}

const InvoiceTransactionInfoDetails = ({ transactions }: Props) => {
  const { t } = useTranslation();

  const transactionItems: RowInvoice[] = useMemo(() => {
    return transactions
      .map(
        (
          transaction: InvoiceTransaction,
          transactionIndex: number
        ): RowInvoice[] => {
          const isWeightUsed = true;
          const isLastTransaction =
            transactionIndex === transactions.length - 1;
          const {
            transactionType,
            transactionNumber,
            materials,
            weight,
            transactionFees = [],
          } = transaction;

          const isWithDeliveryZones =
            transactionType === InvoiceTransactionType.DeliveryToProcessor;

          const title = (
            <div className={styles.type}>
              <span>
                <TireIcon className={styles.icon} />
                <span className={styles.titleText}>
                  {t(
                    `invoices.transactionTypes.${transaction.transactionType}`
                  )}
                </span>
              </span>
              <span>
                <InfoPlate variant="dark" className={styles.statusInfoPlate}>
                  {formatTransactionNumber(transactionNumber)}
                </InfoPlate>
                <span className={styles.titleDate}>
                  {format(
                    new Date(transaction.completedDate),
                    "MMM d, yyyy h:mm a"
                  )}
                </span>
              </span>
            </div>
          );

          const materialRows = isWithDeliveryZones
            ? getDeliveryMaterialsInvoiceInfoRows(
                materials,
                transactionType,
                t,
                false,
                transactionNumber
              )
            : getInvoiceInfoMaterialRows(materials, transactionType, false);

          const transactionFeesRows = getTransactionFeeRows(
            transactionFees,
            t,
            transactionNumber
          );

          const materialsSubTotal = sumBy(
            materials,
            (m: InvoiceMaterial) => m.price
          );
          const feeSubTotal = sumBy(
            transactionFees,
            (fee: InvoiceFee) => fee.calculatedFee
          );

          return [
            {
              key: transactionNumber,
              isTitle: true,
              withBorder: true,
              name: title,
              value: t(
                `invoices.${isWeightUsed ? "weight" : "quantity"}`
              ) as string,
              rate: t("invoices.rate") as string,
              additionalValue: t("invoices.price") as string,
            },
            ...materialRows,
            {
              name: t("invoices.subtotal"),
              key: `${transactionNumber}-subtotal`,
              value: formatWeight(weight),
              rate: "",
              additionalValue: priceToFormat(materialsSubTotal),
              isSubtotal: true,
            },
            ...transactionFeesRows,
            {
              name: t("invoices.transactionSubtotal"),
              key: `${transactionNumber}-transaction-subtotal`,
              value: priceToFormat(materialsSubTotal + feeSubTotal),
              isTotal: true,
              withBoldBorder: !isLastTransaction,
            },
          ];
        }
      )
      .flat();
  }, [t, transactions]);

  return (
    <>
      <table className={styles.invoiceDetails}>
        <tbody>
          {transactionItems.map((row) => {
            return <InvoiceRows key={row.key} {...row} />;
          })}
        </tbody>
      </table>
    </>
  );
};

export default InvoiceTransactionInfoDetails;
